/**
 * Componentes de feedback: Skeleton, LoadingSpinner, EmptyState, NotFoundState, PageHeader.
 */
import type { ReactNode } from 'react';
import { cn } from '@/utils/cn';

export function Skeleton({ className, width, height }: { className?: string; width?: string; height?: string }) {
  return (
    <div
      className={cn('animate-pulse rounded-md bg-slate-200', className)}
      style={{ width, height }}
    />
  );
}

export function LoadingSpinner({ size = 'md', label }: { size?: 'sm' | 'md' | 'lg'; label?: string }) {
  const sizeClasses = { sm: 'h-4 w-4 border-2', md: 'h-8 w-8 border-2', lg: 'h-12 w-12 border-[3px]' };
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-8">
      <div
        className={cn('animate-spin rounded-full border-primary border-b-transparent', sizeClasses[size])}
        aria-label={label || 'Cargando'}
      />
      {label && <span className="text-xs text-slate-500">{label}</span>}
    </div>
  );
}

export function EmptyState({ icon, title, description, actions }: { icon: string; title: string; description?: string; actions?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <span className="material-symbols-outlined text-5xl text-slate-300">{icon}</span>
      <h3 className="mt-4 text-base font-semibold text-slate-700">{title}</h3>
      {description && <p className="mt-1 text-sm text-slate-500 max-w-sm">{description}</p>}
      {actions && <div className="mt-4">{actions}</div>}
    </div>
  );
}

export function NotFoundState({ icon = 'search_off', title, description, onBack, backLabel = 'Volver' }: { icon?: string; title: string; description?: string; onBack?: () => void; backLabel?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <span className="material-symbols-outlined text-6xl text-slate-300">{icon}</span>
      <h2 className="mt-4 text-lg font-semibold text-slate-700">{title}</h2>
      {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      {onBack && (
        <button
          onClick={onBack}
          className="mt-4 inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          {backLabel}
        </button>
      )}
    </div>
  );
}

type PageHeaderProps = {
  title: string;
  icon?: string;
  subtitle?: string;
  actions?: ReactNode;
};

export function PageHeader({ title, icon, subtitle, actions }: PageHeaderProps) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        {icon && <span className="material-symbols-outlined text-2xl text-primary">{icon}</span>}
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
          {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
